import { __ } from '@wordpress/i18n';
import React, { useState, useEffect } from 'react';
import MainPageLayout from '../../components/common/MainPageLayout';
import FormCard from '../../components/common/FormCard';
import { Toast } from '../../components/common/Toast';
import Pagination from '../../components/common/Pagination';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SearchBar from './components/Searchbar';
import StatusTabs from './components/Statustabs';
import PopUpTable from './components/PopUpTable';
import { AddLegalPageIcon } from '../../components/common/SvgIcon';

const AllPopups = ({ page }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    all: 0,
    published: 0,
    draft: 0,
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    pageId: null,
    pageTitle: '',
  });

  // Manual navigation function using hash
  const navigate = (path) => {
    window.location.hash = path;
  };

  const [perPage, setPerPage] = useState(20);

  const fetchPages = async (status, currentPage, search) => {
    setLoading(true);
    try {
      let url = `${LEGAL_PAGES.apiUrl}/legalpage-all-popups?page=${currentPage}&per_page=${perPage}`;

      if (status !== 'all') {
        url += `&status=${status}`;
      }

      if (search.trim()) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        setPages(result.data.pages || []);
        setTotal(result.data.total || 0);
        setTotalPages(result.data.total_pages || 1);
        
        if (result.data.counts) {
          setCounts(result.data.counts);
        }
      } else {
        Toast.error('Failed to load pages');
        setPages([]);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      Toast.error('An error occurred while loading pages');
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pages when filters change
  useEffect(() => {
    fetchPages(activeTab, page, searchQuery);
  }, [activeTab, page, searchQuery]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate('/all-popups'); // Reset to first page on tab change
  };

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    navigate('/all-popups'); // Reset to first page on search
  };

  // Handle delete
  const handleDeleteClick = (pageId, pageTitle) => {
    setDeleteModal({
      isOpen: true,
      pageId,
      pageTitle,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `${LEGAL_PAGES.apiUrl}/legalpage-popup/${deleteModal.pageId}`,
        {
          method: 'DELETE',
          headers: {
            'X-WP-Nonce': LEGAL_PAGES.nonce,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        Toast.success('Popup deleted successfully!');
        setDeleteModal({ isOpen: false, pageId: null, pageTitle: '' });
        fetchPages(activeTab, page, searchQuery); // Refresh the list
      } else {
        Toast.error('Failed to delete popup');
      }
    } catch (error) {
      console.error('Error deleting popup:', error);
      Toast.error('An error occurred while deleting');
    }
  };

   const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, pageId: null, pageTitle: '' });
  };

  // Handle add new page
  const handleAddNewPopup = () => {
    adl_legal_pages__modal(true);
    navigate('/add-new-popup');
    setTimeout(() => {
      adl_legal_pages__modal(false);
    }, 500);
  };

  return (
    <MainPageLayout
      title="All Popups"
      description="Manage all your created popups"
      button={
        <button
          className="adl-add-button"
          onClick={handleAddNewPopup}
        >
          <AddLegalPageIcon />
          Add New Popup
        </button>
      }
    >
      <FormCard className='min-h-screen'>
        <div className='flex items-center gap-4 justify-start'>
          <SearchBar onSearch={handleSearch} />

          {/* Status Tabs */}
          <StatusTabs
            activeTab={activeTab}
            counts={counts}
            onTabChange={handleTabChange}
          />
        </div>

        {/* Table */}
        <PopUpTable
          pages={pages}
          loading={loading}
          onDelete={handleDeleteClick}
        />

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              baseSlug="all-popups"
              current={page}
              total={totalPages}
            />
          </div>
        )}
      </FormCard>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Page"
        message={`Are you sure you want to delete "${deleteModal.pageTitle}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={false}
      />
    </MainPageLayout>
  );
};

export default AllPopups;